import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Alert,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ApplicationBody from '@/components/application_components/ApplicationBody';
import ApplicationHeader from '@/components/application_components/header/ApplicationHeader';
import { Spinner } from '@/components/common/Spinner';
import DeadlineBanner from '@/components/dashboard_components/applicant_dashboard/DeadlineBanner';
import Header from '@/components/header/Header';
import { NotFound } from '@/components/NotFound';
import {
  CurrentApplicationContext,
  CurrentApplicationProvider,
} from '@/Contexts/CurrentApplicationContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { UsersContext } from '@/Contexts/UsersContext';
import { Locales, UserType } from '@/models/User';

const languageOptions: Array<{ value: Locales; label: string }> = [
  { value: 'enUS', label: 'English' },
  { value: 'esES', label: 'Español' },
  { value: 'frFR', label: 'Français' },
  { value: 'ptBR', label: 'Português' },
  { value: 'itIT', label: 'Italiano' },
];

function ApplicationPreviewContent() {
  const { currentStandardInfo, applicationLoading, questions, applicationCycle } =
    useContext(CurrentApplicationContext);
  const { locale, translatedText, setOverrideLocale, clearOverrideLocale } =
    useContext(LocaleContext);
  const { userCurrent } = useContext(UsersContext);

  const navigate = useNavigate();

  const canRenderTheApplication = !applicationLoading && locale != null;

  const scrollableRef = useRef<HTMLDivElement>(null);

  if (
    userCurrent?.userType !== UserType.Adf &&
    userCurrent?.userType !== UserType.NationalProgrammeLead
  ) {
    return <NotFound />;
  }

  useEffect(() => {
    if (currentStandardInfo?.standardId != null && scrollableRef.current != null) {
      scrollableRef.current.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }
  }, [currentStandardInfo?.standardId]);

  useEffect(() => {
    return () => {
      clearOverrideLocale();
    };
  }, [clearOverrideLocale]);

  return (
    <Box display='flex' flexDirection='column' height='100vh' width='100vw'>
      <DeadlineBanner />
      <Header />
      {!canRenderTheApplication ? (
        <Spinner />
      ) : (
        <Box
          ref={scrollableRef}
          sx={{
            overflowX: 'hidden',
            scrollbarGutter: 'stable',
            width: '100vw',
            justifyContent: 'center',
            display: 'flex',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              width: '1400px',
            }}
          >
            <Box
              sx={{
                mt: 2,
                px: 2,
                py: 1.5,
                borderRadius: 1,
                backgroundColor: 'sandShade1.main',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Typography variant='title1'>
                  {translatedText.applicationPreview.programLabel}:{' '}
                  {questions.programName ?? translatedText.applicationPreview.unknownValue} -{' '}
                  {applicationCycle?.year ?? translatedText.applicationPreview.unknownValue}
                </Typography>
                <FormControl size='small' sx={{ minWidth: 185 }}>
                  <InputLabel id='application-preview-language-label'>
                    {translatedText.header.tooltipLanguage}
                  </InputLabel>
                  <Select
                    labelId='application-preview-language-label'
                    id='application-preview-language-select'
                    label={translatedText.header.tooltipLanguage}
                    value={locale ?? 'enUS'}
                    onChange={(event) => setOverrideLocale(event.target.value)}
                  >
                    {languageOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {userCurrent?.userType === UserType.Adf && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant='secondary'
                    size='small'
                    startIcon={<ArrowBackIcon fontSize='small' />}
                    onClick={() => navigate('/admin')}
                  >
                    Back to Admin
                  </Button>
                </Box>
              )}
            </Box>
            <Alert severity='info'>{translatedText.applicationPreview.previewModeMessage}</Alert>
            <ApplicationHeader />
            <ApplicationBody />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default function ApplicationPreview() {
  const { applicationCycleId } = useParams();
  const { userType } = useContext(UsersContext);
  const parsedCycleId = Number(applicationCycleId);
  const previewCycleId = Number.isFinite(parsedCycleId) ? parsedCycleId : undefined;

  if (userType !== UserType.Adf && userType !== UserType.NationalProgrammeLead) {
    return <NotFound />;
  }

  return (
    <CurrentApplicationProvider
      key={`preview-${applicationCycleId}`}
      appid={undefined}
      previewCycleId={previewCycleId}
    >
      <ApplicationPreviewContent />
    </CurrentApplicationProvider>
  );
}
